"use client";

import { useEffect } from "react";
import Script from "next/script";

const GTM_ID = "GTM-MBTFXKJD";

declare global {
  interface Window {
    google_tag_manager?: unknown;
  }
}

/**
 * GTM (and the GA4 tag it loads) belongs to the public marketing pages
 * only, so it is rendered from `app/(marketing)/layout.tsx`, never from
 * the root layout. Mount this via `<MarketingGoogleTagManager />`.
 */
export function GoogleTagManager() {
  return (
    <Script
      id="google-tag-manager"
      strategy="afterInteractive"
      dangerouslySetInnerHTML={{
        __html: `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${GTM_ID}');
        `,
      }}
    />
  );
}

export function GoogleTagManagerNoscript() {
  return (
    <noscript>
      <iframe
        src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
        height="0"
        width="0"
        style={{ display: "none", visibility: "hidden" }}
      />
    </noscript>
  );
}

/**
 * A loaded GTM container cannot be unloaded, and GA4 records a page
 * view on every client-side route change. When the marketing layout
 * unmounts (a soft navigation to /sign-in, /o/..., etc.) this forces
 * a full load of the destination so the app document is served
 * without GTM.
 */
export function useLeaveGtmScopeWithFullLoad() {
  useEffect(() => {
    return () => {
      if (!window.google_tag_manager) return;
      // Defer until the router has committed the new URL.
      setTimeout(() => window.location.reload(), 0);
    };
  }, []);
}

export function MarketingGoogleTagManager() {
  useLeaveGtmScopeWithFullLoad();

  return (
    <>
      <GoogleTagManagerNoscript />
      <GoogleTagManager />
    </>
  );
}
